import React, { useContext, useMemo, useState } from 'react';

import { Alert } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { useUpdateProject } from '@hooks/API/Project/UseProject';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWTextAreaCharCounter, NWInput } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const EditAccountingDetailsDrawer = ({ project, onClose }) => {
    useFormFieldFocus('PONumber');
    const { mutateAsync: onUpdateProject } = useUpdateProject();
    const [touchForm, setTouchForm] = useState(false);
    const [errorSaving, setErrorSaving] = useState(null);

    let initialValues = {
        PONumber: project.PONumber,
        AccountingNote: project.AccountingNote,
    };

    const saveProject = async (values, setSubmitting) => {
        // Only check for changes in AccountingNote and PONumber
        const accountingNoteChanged = initialValues.AccountingNote !== values.AccountingNote;
        const poNumberChanged = initialValues.PONumber !== values.PONumber;
        const somethingChanged = accountingNoteChanged || poNumberChanged;

        if (somethingChanged) {
            const projectData = [];

            // Only include fields that have actually changed
            if (poNumberChanged) {
                projectData.push({
                    name: 'PONumber',
                    value: values.PONumber,
                    changed: true,
                    errormessage: '',
                    updated: true,
                });
            }
            if (accountingNoteChanged) {
                projectData.push({
                    name: 'AccountingNote',
                    value: values.AccountingNote,
                    changed: true,
                    errormessage: '',
                    updated: true,
                });
            }

            try {
                await onUpdateProject({ projectId: project.ID, data: projectData });
                setTouchForm(false);
                onClose();
            } catch (error) {
                setErrorSaving('There was an error saving the project. Please try again.');
                setSubmitting(false);
            }
        } else {
            setErrorSaving('No changes to save.');
            setTouchForm(false);
            setSubmitting(false);
        }
    };

    return (
        <NwDrawer touchForm={touchForm} onTouchForm={setTouchForm} title={'Edit Project'} onClose={onClose}>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    saveProject(values, setSubmitting);
                }}
                validationSchema={Yup.object().shape({})}
            >
                {({ isSubmitting, values, handleSubmit }) => (
                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Field
                            component={NWInput}
                            label="P.O. Number"
                            name="PONumber"
                            type="text"
                            value={values.PONumber}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Accounting Note"
                            name="AccountingNote"
                            type="text"
                            value={values.AccountingNote}
                        />
                        {errorSaving && (
                            <Alert
                                style={{ marginTop: '1rem' }}
                                message="Error"
                                description={errorSaving}
                                type="error"
                                showIcon
                            />
                        )}
                        <br />
                        <NwFormButtonsBar
                            left={
                                <NwCancelButton
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setTouchForm(false);
                                        onClose();
                                    }}
                                />
                            }
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                    </NwForm>
                )}
            </Formik>
        </NwDrawer>
    );
};

export default EditAccountingDetailsDrawer;
